import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import * as execApi from '../services/executiveSummaryApi';

function HealthGauge({ score }) {
  if (score === null) return <p style={{ color: 'var(--color-text-muted)' }}>No stock data yet.</p>;
  const color = score >= 80 ? 'var(--color-success)' : score >= 60 ? 'var(--color-warning)' : 'var(--color-danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
      <div style={{ position: 'relative', width: 100, height: 100 }}>
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="var(--color-surface-alt)" strokeWidth="10" />
          <circle
            cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={`${(score / 100) * 264} 264`}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <span className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color }}>{score}</span>
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>/ 100</span>
        </div>
      </div>
    </div>
  );
}

function SeverityDot({ severity }) {
  const colors = { critical: 'var(--color-danger)', high: '#E0855F' };
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: colors[severity], flexShrink: 0, marginTop: 6 }} />;
}

export default function ExecutiveSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    execApi.getExecutiveSummary()
      .then(setData)
      .catch((err) => setError(err.message || 'Could not load the executive summary.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h1>AI Executive Summary</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        A always-current synthesis assembled from real computed data — company health score,
        the most urgent open issues across every AI module, and the sharpest recent demand
        move — not generated free-text.
      </p>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>}

      {data && (
        <>
          <Card>
            <p style={{ fontSize: 'var(--font-size-base)', lineHeight: 1.6, margin: 0 }}>{data.narrative}</p>
          </Card>

          <div className="responsive-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-5)' }}>
            <Card title="Inventory Health">
              <HealthGauge score={data.healthScore} />
              {data.healthBreakdown && (
                <div style={{ marginTop: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  <div>{data.healthBreakdown.totalDesigns} tracked designs</div>
                  <div style={{ color: data.healthBreakdown.negativeCount > 0 ? 'var(--color-danger)' : undefined }}>{data.healthBreakdown.negativeCount} negative</div>
                  <div style={{ color: data.healthBreakdown.lowCount > 0 ? 'var(--color-warning)' : undefined }}>{data.healthBreakdown.lowCount} low stock</div>
                  <div>{data.healthBreakdown.deadCount} dead stock</div>
                </div>
              )}
            </Card>

            <Card title="Top Movers">
              {data.topMovers.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No sharp demand accelerations detected recently.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                  {data.topMovers.map((m) => (
                    <li key={m.designId} style={{ marginBottom: 6 }}>
                      <strong>{m.designName}</strong> — demand up <span className="data-figure" style={{ color: 'var(--color-success)', fontWeight: 700 }}>{m.pctChange}%</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card title={`Critical & High-Priority Issues (${data.criticalIssues.length})`}>
            {data.criticalIssues.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Nothing critical or high-priority is open right now.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {data.criticalIssues.map((issue) => (
                  <li key={`${issue.source}-${issue.id}`} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <SeverityDot severity={issue.severity} />
                    <div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>
                        {issue.source.replace('_', ' ')} · {issue.type}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-sm)' }}>{issue.message}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
